import { Injectable } from "@angular/core";
import { v4 as uuid_v4 } from "uuid";
import { Stack } from "../../basic/stack";
import { Identifier } from "../../models/basic.models";
import { AnyDrawCommand, CellKind } from "./drawing.models";
import {
    HorizontalAlignment,
    Axis,
    AXIS_PADDING_MAPPING,
    AXIS_POSITION_MAPPING,
    AXIS_SIZE_MAPPING,
    DEFAULT_LAYOUT,
    LayoutDirection,
    SimpleLayoutElement,
    SizingType,
    VerticalAlignment,
    ALIGNMENT_CONFIG_MAPPING,
    Alignment,
    AXIS_MAX_SIZE_MAPPING
} from "./layout-manager.models";
import { LayoutUtils } from "./layout-utils";
import { TRANSPARENT_COLOR } from "../../constants/colors";

/**
 * Serviço responsável por criar e gerenciar a disposição
 * de elementos visuais para serem desenhados (i.e. Layout).
 *
 * Este serviço faz o cálculo do tamanho e posicionamento dos elementos de acordo com
 * os parâmetros fornecidos na criação de cada elemento.
 *
 * Ao final da criação de um Layout, é retornada uma lista ordenada dos comandos
 * de desenho necessários para formar o layout desejado. Estes comandos podem ser
 * repassados para código responsável por desenhar os elementos na tela.
 *
 * Atualmente usamos o MaxGraph para isso, mas com mínimas adaptações, seria possível
 * suportar outras formas de desenhar elementos.
 *
 * Inspiração: https://www.youtube.com/watch?v=by9lQvpvMIc
 */
@Injectable()
export class LayoutManager {
    private static readonly ROOT_NODE_NAME: string = "Root Container";

    private elementsMap: Map<Identifier, SimpleLayoutElement>;
    private openElementStack: Stack<SimpleLayoutElement>;
    private rootNode: SimpleLayoutElement;
    private isDebugEnabled: boolean;

    public commandMap: Map<Identifier, AnyDrawCommand>;

    constructor() {
        this.clear();
    }

    /**
     * Limpa todos os dados salvos do layout atual.
     *
     * Útil para poder começar um novo layout do zero.
     */
    clear() {
        this.elementsMap = new Map();
        this.commandMap = new Map();
        this.openElementStack = new Stack();
        this.rootNode = { name: LayoutManager.ROOT_NODE_NAME };
        this.isDebugEnabled = false;
    }

    /**
     * Inicia a construção de um layout, criando um elemento raiz.
     *
     * Tudo que for criado antes que este layout seja finalizado será
     * considerado como filho do elemento raiz deste layout.
     *
     * @param element objeto com as propriedades que devem ser utilizadas na criação do elemento raiz.
     */
    beginLayout(element?: SimpleLayoutElement) {
        this.clear();

        this.rootNode = element || this.rootNode;

        this.openElement(this.rootNode);
    }

    /**
     * Finaliza a construção de um layout.
     * Neste momento é possível fazer os últimos cálculos necessários para concluir o layout.
     *
     * @returns Lista ordenada de comandos usados para desenhar os elementos.
     */
    endLayout(): AnyDrawCommand[] {
        this.closeElement();

        return this.calculateFinalLayoutSimplified();
    }

    /**
     * Cria um layout.
     *
     * Assinatura extra para sobrecarga da função `createLayout()`, ver implementação.
     *
     * Esta assinatura permite receber apenas um callback, e portanto usar as configurações
     * padrões para o elemento raiz.
     *
     * @param callback callback para ser inserido entre `beginLayout()` e `endLayout()`.
     */
    createLayout(callback: Function): AnyDrawCommand[];

    /**
     * Cria um layout.
     *
     * Assinatura extra para sobrecarga da função `createLayout()`, ver implementação.
     *
     * Esta assinatura permite receber um elemento e um callback. Este elemento é repassado para
     * o `beginLayout()` para que ele sirva como elemento raiz.
     *
     * @param element elemento a ser usado com raiz do layout.
     * @param callback callback para ser inserido entre `beginLayout()` e `endLayout()`.
     */
    createLayout(element: SimpleLayoutElement, callback: Function): AnyDrawCommand[];

    /**
     * Cria um layout.
     *
     * Esta função é um atalho para as funções individuais `beginLayout()`
     * e `endLayout()`, encapsulando o callback entre as respectivas chamadas.
     *
     * Tudo que for criado dentro do callback recebido será
     * considerado como filho do elemento raiz deste layout.
     *
     * Esta função possui overrides da sua assinatura de forma que pode ser chamada
     * recebendo apenas um callback, ou um elemento e um callback. No caso em que
     * a função recebe um elemento além do callback, ela repassa esse elemento para
     * o `beginLayout()` para que ele sirva como elemento raiz.
     *
     * Devido ao modo que o Typescript dá suporte a sobrecarga de funções,
     * a implementação precisa ser feita uma única vez numa função que seja
     * capaz de lidar com todas outras possíveis assinaturas. Por isso que o
     * primeiro parâmetro é um union entre SimpleLayoutElement e Function.
     *
     * @param maybeElement  parâmetro que pode ser o elemento a ser usado com raiz do layout,
     *                      ou o callback para ser inserido entre `beginLayout()` e `endLayout()`.
     * @param maybeCallback parâmetro que pode ser o callback para ser inserido entre `beginLayout()` e `endLayout()`,
     *                      ou pode não existir.
     */
    createLayout(maybeElement: SimpleLayoutElement | Function, maybeCallback?: Function): AnyDrawCommand[] {
        const callback = (maybeElement instanceof Function) ? maybeElement : maybeCallback;
        const element  = (maybeElement instanceof Function) ? undefined    : maybeElement;

        this.beginLayout(element);

        callback();

        return this.endLayout();
    }

    /**
     * Inicia a construção de um elemento usando como base o elemento passado por parâmetro.
     *
     * Tudo que for criado antes que este elemento seja finalizado será considerado como filho deste elemento.
     *
     * @param element objeto com as propriedades que devem ser utilizadas na criação do elemento que está sendo criado.
     */
    openElement(element: SimpleLayoutElement) {
        const parent = this.openElementStack.peek();

        this.initializeElement(element, parent);

        if (parent) {
            element.parent.children.push(element);
        }

        this.openElementStack.push(element);
        this.elementsMap.set(element.id, element);
    }

    /**
     * Finaliza a construção do último elemento aberto.
     * Neste momento é possível fazer os cálculos iniciais de tamanho do
     * elemento recém finalizado em relação a seus filhos.
     *
     * @returns elemento criado.
     */
    closeElement() {
        const element = this.openElementStack.pop();
        this.calculateFitSizing(element);

        return element;
    }

    /**
     * Cria um elemento.
     *
     * Esta função é um atalho para as funções individuais `openElement()`
     * e `closeElement()`, encapsulando o callback entre as respectivas chamadas.
     *
     * Tudo que for criado dentro do callback recebido será considerado como filho deste elemento.
     *
     * @param element propriedades iniciais do elemento que está sendo criado.
     * @param callback callback para ser inserido entre `openElement()` e `closeElement()`.
     *
     * @returns elemento criado conforme em `closeElement()`.
     */
    createElement(element: SimpleLayoutElement, callback?: Function): SimpleLayoutElement {
        this.openElement(element);

        if (callback) {
            callback();
        }

        return this.closeElement();
    }

    /**
     * Altera elemento recebido por parâmetro para garantir que ele tenha tido
     * as propriedades necessárias inicializadas corretamente.
     *
     * Inicializa as propriedades de um elemento de acordo com os valores padrão.
     *
     * @param element elemento a ter suas propriedades inicializadas.
     */
    private initializeElement(element: SimpleLayoutElement, parent?: SimpleLayoutElement) {
        if (!element.id) {
            element.id = uuid_v4() as Identifier;
        }

        const defaultSizing = (element.width) ? SizingType.FIXED : SizingType.FIT;
        const isParentFloating = (parent && parent.isFloating);

        if (parent) {
            element.parent = parent;
        }

        element.kind       = element.kind       || CellKind.VERTEX;
        element.direction  = element.direction  || DEFAULT_LAYOUT;
        element.sizing     = element.sizing     || defaultSizing;
        element.width      = element.width      || 0;
        element.height     = element.height     || 0;
        element.childGap   = element.childGap   || 0;
        element.children   = element.children   || [];
        element.style      = element.style      || { fillColor: TRANSPARENT_COLOR };
        element.isFloating = element.isFloating ?? isParentFloating ?? false;

        element.offset     = LayoutUtils.initializeOffset(element.offset);
        element.alignment  = LayoutUtils.initializeAlignment(element.alignment);
        element.padding    = LayoutUtils.initializePadding(element.padding);
    }

    /**
     * Calcula a largura e altura dos elementos que precisam pelo menos se
     * ajustar ao tamanho do seus filhos (i.e. SizingType.FIT, SizingType.GROW).
     *
     * Este cálculo é naturalmente executado em ordem RBF (i.e. Reverse Breadth-First)
     * devido ao fluxo de abertura e fechamento de elementos. Ver `closeElement()`.
     */
    private calculateFitSizing(element: SimpleLayoutElement) {
        const parent = element.parent;
        const padding = element.padding;
        const childGap = Math.max(this.getNonFloatingChildren(element).length - 1, 0) * element.childGap;

        if (element.sizing !== SizingType.FIXED) {
            element.width += padding.left + padding.right;
            element.height += padding.top + padding.bottom;
        }

        if (!element.isFloating) {
            element[AXIS_SIZE_MAPPING[element.direction].ALONG] += childGap;

            if (parent && parent.sizing !== SizingType.FIXED) {
                const parentAxis = AXIS_SIZE_MAPPING[parent.direction];

                parent[parentAxis.ALONG] += element[parentAxis.ALONG];
                parent[parentAxis.ACROSS] = Math.max(element[parentAxis.ACROSS], parent[parentAxis.ACROSS]);
            }
        }
    }

    /**
     * Recebe um elemento e filtra os filhos não flutuantes.
     *
     * @param element elemento pai
     * @returns lista de elementos de layout que satisfaçam a condição de não serem flutuantes.
     */
    private getNonFloatingChildren(element: SimpleLayoutElement): SimpleLayoutElement[] {
        return element.children.filter((child) => !child.isFloating);
    }

    /**
     * Recebe um elemento e filtra os filhos que não possuem a opção GROW.
     *
     * @param element elemento pai
     * @returns lista de elementos de layout que satisfaçam a condição de não possuírem a opção GROW.
     */
    private getGrowingChildren(element: SimpleLayoutElement): SimpleLayoutElement[] {
        return element.children.filter((child) => child.sizing === SizingType.GROW);
    }

    /**
     * Calcula a largura e altura final de todos elementos
     * que possuem a opção de crescer (i.e. SizingType.GROW).
     */
    private calculateGrowing() {
        for(const element of this.elementsMap.values()) {
            const growable = this.getGrowingChildren(element);

            this.growElements(element, growable, Axis.ALONG);
            this.growElements(element, growable, Axis.ACROSS);
        }
    }

    /**
     * Calcula tamanho dos elementos que possuem a opção de crescer (i.e. SizingType.GROW)
     *
     * @param parent elemento pai
     * @param growable lista de elementos definidos com a opção GROW
     */
    private growElements(parent: SimpleLayoutElement, growable: SimpleLayoutElement[], axis: Axis) { // NOSONAR
        if (growable.length > 0) {
            let { remaining, sizeProperty, maxSizeProperty } = this.calculateRemainingSize(axis, parent, growable);

            while (remaining > Number.MIN_VALUE) {
                let smallest = growable[0][sizeProperty];
                let secondSmallest = Infinity;
                let sizeToAdd = remaining;

                for (const child of growable) {
                    if (child[sizeProperty] < smallest) {
                        secondSmallest = smallest;
                        smallest = child[sizeProperty];
                    }

                    if (child[sizeProperty] > smallest) {
                        secondSmallest = Math.min(secondSmallest, child[sizeProperty]);
                        sizeToAdd = secondSmallest - smallest;
                    }
                }

                sizeToAdd = Math.min(sizeToAdd, remaining / growable.length);

                for (const child of growable) {
                    if (child[sizeProperty] === smallest) {
                        child[sizeProperty] += sizeToAdd;
                        remaining -= sizeToAdd;
                    }

                    if (child[sizeProperty] > child[maxSizeProperty]) {
                        const index = growable.indexOf(child);

                        if (index > -1) {
                            growable.splice(index, 1);
                        }

                        child[sizeProperty] = child[maxSizeProperty]
                    }
                }
            }
        }
    }

    /**
     * Calcula os posicionamentos de cada elemento.
     *
     * Caso seja necessário suportar opções de alinhamento, elas devem ser suportadas aqui.
     */
    private calculatePositionsAndAlignments(element: SimpleLayoutElement, leftOffset: number = 0, topOffset: number = 0) {
        element.x = leftOffset;
        element.y = topOffset;

        leftOffset += element.padding.left;
        topOffset += element.padding.top;

        for(const child of element.children) {
            this.calculatePositionsAndAlignments(child, leftOffset, topOffset);

            // Executa depois que toda sub-árvore de `child` tiver sido processada
            if (child.isFloating) {
                child.x = leftOffset + child.offset.x;
                child.y = topOffset + child.offset.y;

                continue;
            }

            if (element.direction === LayoutDirection.LEFT_TO_RIGHT) {
                leftOffset += child.width + element.childGap;
            } else {
                topOffset += child.height + element.childGap;
            }
        }

        this.calculateAlignments(Axis.ACROSS, element);
        this.calculateAlignments(Axis.ALONG, element);
    }

    /**
     * Calcula o alinhamento dos elementos filhos em relação a seu elemento pai.
     */
    calculateAlignments(axis, parent: SimpleLayoutElement) {
        const alignmentProperty = ALIGNMENT_CONFIG_MAPPING[parent.direction][axis];

        const { remaining, positionProperty } = this.calculateRemainingSize(axis, parent);
        let offset = this.calculateAlignmentOffset(parent.alignment[alignmentProperty], remaining);

        for (const child of parent.children) {
            if (axis === Axis.ACROSS) {
                const { remaining } = this.calculateRemainingSize(axis, parent);

                offset = this.calculateAlignmentOffset(parent.alignment[alignmentProperty], remaining);
            }

            child[positionProperty] += offset;
        }
    }

    /**
     * Calcula o tamanho restante ainda não utilizado.
     */
    private calculateRemainingSize(axis: Axis, parent: SimpleLayoutElement, currentElements?: SimpleLayoutElement[]) {
        const sizeProperty = AXIS_SIZE_MAPPING[parent.direction][axis];
        const maxSizeProperty = AXIS_MAX_SIZE_MAPPING[parent.direction][axis];
        const positionProperty = AXIS_POSITION_MAPPING[parent.direction][axis];
        const [leftOrTop, rightOrBottom] = AXIS_PADDING_MAPPING[parent.direction][axis];

        let remaining = parent[sizeProperty];
        remaining -= parent.padding[leftOrTop] + parent.padding[rightOrBottom];

        if (axis === Axis.ALONG) {
            for (const child of parent.children) {
                remaining -= child[sizeProperty];
            }

            remaining -= Math.max(parent.children.length - 1, 0) * parent.childGap;
        } else if (currentElements && currentElements.length > 0) {
            remaining -= Math.max(...currentElements.map((item) => item[sizeProperty]));
        } else {
            remaining -= Math.max(...parent.children.map((item) => item[sizeProperty]));
        }

        return { remaining, sizeProperty, maxSizeProperty, positionProperty };
    }

    /**
     * Calcula o offset de alinhamento.
     *
     * Se for um alinhamento central (i.e. VerticalAlignment.MIDDLE ou HorizontalAlignment.CENTER)
     * então o offset precisa ser metade to tamanho restante.
     *
     * Se o alinhamento for o oposto do padrão do layout (i.e. VerticalAlignment.BOTTOM ou HorizontalAlignment.RIGHT)
     * então o offset precisa receber a totalidade do tamanho restante.
     */
    private calculateAlignmentOffset(alignment: Alignment, remaining: number) {
        let offset = 0;

        if (alignment === HorizontalAlignment.CENTER || alignment === VerticalAlignment.MIDDLE) {
            offset = remaining / 2;
        }

        if (alignment === HorizontalAlignment.RIGHT || alignment === VerticalAlignment.BOTTOM) {
            offset = remaining;
        }

        return offset;
    }

    /**
     * Gera o conjunto de comandos que devem ser forncecidos para quem
     * ficar responsável por desenhar os elementos na tela (e.g. MaxGraph).
     */
    generateDrawCommands(element: SimpleLayoutElement): AnyDrawCommand[] {
        const commands: AnyDrawCommand[] = [];

        for(const child of this.elementsMap.values()) {
            switch (child.kind) {
                case CellKind.VERTEX:
                    commands.push({
                        id: child.id,
                        name: child.name,
                        kind: child.kind,
                        parameters: {
                            x: child.x,
                            y: child.y,
                            width: child.width,
                            height: child.height,
                            style: child.style,
                            value: child.value,
                            custom: child.custom
                        }
                    })
                    break;
                case CellKind.EDGE:
                    commands.push({
                        id: child.id,
                        name: child.name,
                        kind: child.kind,
                        parameters: {
                            parent: child.parent.id,
                            source: child.source,
                            target: child.target,
                            style: child.style,
                            value: child.value,
                            custom: child.custom
                        }
                    })
                    break;
            }

            commands.push(...this.createDebugDrawCommands(child))
        }

        for (const command of commands) {
            this.commandMap.set(command.id, command);
        }

        return commands;
    }

    /**
     * Gera um conjunto extra de comandos para poder possibilitar um debug visual
     * do LayoutManager.
     */
    private createDebugDrawCommands(currentElement: SimpleLayoutElement): AnyDrawCommand[] {
        const commands: AnyDrawCommand[] = [];

        if (this.isDebugEnabled) {
            console.debug(`${currentElement.name} command:`, commands[commands.length-1]);

            commands.push(...this.buildPaddingDebugCommand(currentElement));
        }

        return commands;
    }

    private buildPaddingDebugCommand(element: SimpleLayoutElement): AnyDrawCommand[] {
        const commands = [];
        const paddingDebugColor = "rgba(0, 80, 255, 0.5)";

        for (const [orientation, value] of Object.entries(element.padding)) {
            if (value > 0)  {
                let x = element.x;
                let y = element.y;
                let width = element.width;
                let height = element.height;

                if (orientation === "left") {
                    width = value;
                }

                if (orientation === "top") {
                    height = value;
                }

                if (orientation === "right") {
                    width = value;
                    x += element.width - value;
                }

                if (orientation === "bottom") {
                    height = value;
                    y += element.height - value;
                }

                commands.push({
                    id: element.id,
                    kind: CellKind.VERTEX,
                    parameters: {
                        x,
                        y,
                        width,
                        height,
                        style: {
                            fillColor: paddingDebugColor
                        }
                    }
                })
            }
        }

        return commands;
    }

    /**
     * Executa os passes restantes necessários na árvore de elementos.
     *
     * Esta é a ordem de execução dos passes:
     *
     *     1. Calculate Fit Sizing
     *     2. Calculate Growing
     *     3. Calculate Positions and Alignments
     *     4. Generate Draw Commands
     *
     * Ao chegar nesta função, o primeiro passe já foi calculado.
     * Ele é executado em ordem RBF (i.e. Reverse Breadth-First).
     * Ver `calculateFitSizing()` e `closeElement()`.
     */
    private calculateFinalLayoutSimplified(): AnyDrawCommand[] {
        this.calculateGrowing();
        this.calculatePositionsAndAlignments(this.rootNode);

        return this.generateDrawCommands(this.rootNode);
    }
}
